import { Box, CircularProgress } from '@material-ui/core'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { getPurchasesChangeLogs } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table, TableRowRenderer } from 'react-virtualized'
import { Logs, SPLITTING_STRATEGY_CONVERSIONS } from 'shared'
import { getFormattedDate } from '../backoffice/BackofficePresenter'

export const PurchaseChangeLogsPresenter = (): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const [logs, setLogs] = useState<Logs[]>([])
  const [showLoading, setShowLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLastPage, setIsLastPage] = useState<boolean>(false)
  const { t } = useTranslation()

  const tableHeight: number = (window.innerHeight * 50) / 100

  const fetchLogs = async () => {
    setShowLoading(true)
    try {
      const { logs, hasNextPage } = await getPurchasesChangeLogs(currentPage)
      setLogs(logs)
      setIsLastPage(!hasNextPage)
    } catch (e) {
      enqueueSnackbar('Error loading logs', { variant: 'error' })
    } finally {
      setShowLoading(false)
    }
  }

  useEffect(() => {
    fetchLogs()
  }, [currentPage])

  const rowRenderer: TableRowRenderer = ({ key, style, columns, className, rowData }) => {
    rowData

    return (
      <div key={key} style={style} className={`${className}`}>
        {columns}
      </div>
    )
  }
  return (
    <div className="taxation-root" style={{ overflow: 'auto' }}>
      {showLoading ? (
        <div className="w-100 d-flex flex-column align-items-center ">
          <h6 className="pb-5">{t('common.loading')}</h6>
          <CircularProgress size={100} />
        </div>
      ) : (
        <>
          <h3>{t('taxation.logsTab')}</h3>
          <div className="mt-3 responsive-table-container">
            <AutoSizer disableHeight>
              {({ width }) => (
                <Table
                  width={Math.max(width, 1200)}
                  height={tableHeight}
                  headerHeight={40}
                  rowHeight={40}
                  rowCount={logs.length}
                  rowGetter={({ index }) => logs[index]}
                  className={`picking-table`}
                  rowRenderer={rowRenderer}
                  overscanRowCount={5}
                >
                  <Column
                    width={width * 0.1}
                    label={t('common.date')}
                    dataKey="purchaseDate"
                    cellRenderer={({ rowData: a }) => (
                      <Box className="text-wrap">
                        <span>{getFormattedDate(a.createdAt)}</span>
                      </Box>
                    )}
                  />

                  <Column
                    width={width * 0.1}
                    label={t('taxation.purchaseEvent')}
                    dataKey="purchaseEvent"
                    headerRenderer={() => (
                      <Box width={width * 0.1}>
                        <span>{t('taxation.purchaseEvent')}</span>
                      </Box>
                    )}
                    cellRenderer={({ rowData: a }) => (
                      <Box>
                        <div className="d-flex flex-column">
                          <span>{new Date(a.purchaseEvent.purchaseDate).toDateString()}</span>
                          <span> {a.purchaseEvent.lotNumber}</span>
                        </div>
                      </Box>
                    )}
                  />

                  <Column
                    width={width * 0.15}
                    label={t('taxation.seller.seller')}
                    dataKey="seller"
                    cellRenderer={({ rowData: a }) => (
                      <Box>
                        <div className="d-flex flex-column">
                          <span>{a.purchaseEvent.seller.name} </span>
                          <span>{a.purchaseEvent.seller.vatNumber}</span>
                        </div>
                      </Box>
                    )}
                  />

                  <Column
                    width={width * 0.15}
                    label={t('card.name')}
                    dataKey="item"
                    cellRenderer={({ rowData: a }) => (
                      <Box className="text-wrap">{a.purchase.ptProduct.name}</Box>
                    )}
                  />

                  <Column
                    width={width * 0.15}
                    label={t('card.expansion')}
                    dataKey="expansion"
                    cellRenderer={({ rowData: a }) => (
                      <Box className="text-wrap">
                        ({a.purchase.ptProduct.expansionCode}) {a.purchase.ptProduct.expansionName}
                      </Box>
                    )}
                  />

                  <Column
                    width={width * 0.1}
                    label={t('transaction.properties')}
                    dataKey="properties"
                    cellRenderer={({ rowData: a }) => (
                      <Box display="flex" width="150px" alignItems={'center'}>
                        <ArticleAttributes article={a.purchase.ptProduct} iconHeight={17} />
                      </Box>
                    )}
                  />

                  <Column
                    width={width * 0.1}
                    label={`${t('orders.quantity')} ${t('transaction.fromTo')}`}
                    headerRenderer={() => (
                      <Box width={width * 0.1}>
                        <span>{`${t('orders.quantity')} ${t('transaction.fromTo')}`}</span>
                      </Box>
                    )}
                    dataKey="quantity"
                    cellRenderer={({ rowData: a }) => (
                      <Box width="100px" textAlign="center">
                        {a.quantityFrom !== a.quantityTo ? (
                          <span>
                            {a.quantityFrom} &rArr; {a.quantityTo}{' '}
                          </span>
                        ) : (
                          <span></span>
                        )}
                      </Box>
                    )}
                  />

                  <Column
                    width={width * 0.1}
                    label={`${t('card.price')} ${t('transaction.fromTo')}`}
                    dataKey="price"
                    headerRenderer={() => (
                      <Box width={width * 0.1}>
                        <span>{`${t('card.price')} ${t('transaction.fromTo')}`}</span>
                      </Box>
                    )}
                    cellRenderer={({ rowData: a }) => (
                      <Box width="100px" textAlign="center">
                        {a.priceFrom !== a.priceTo ? (
                          <span>
                            {a.priceFrom} &rArr; {a.priceTo}{' '}
                          </span>
                        ) : (
                          <span></span>
                        )}
                      </Box>
                    )}
                  />

                  <Column
                    width={400}
                    label={t('taxation.priceChangeSource')}
                    headerRenderer={() => (
                      <Box width={400}>
                        <span>{t('taxation.priceChangeSource')}</span>
                      </Box>
                    )}
                    dataKey="changeReason"
                    cellRenderer={({ rowData: a }) => (
                      <Box className="text-wrap">
                        {a.priceFrom !== a.priceTo ? (
                          <span className="text-wrap">
                            {
                              SPLITTING_STRATEGY_CONVERSIONS.find(
                                (item) => item.backendTypeCast === a.creationCauseFrom
                              )?.label
                            }{' '}
                            &rArr;{' '}
                            {
                              SPLITTING_STRATEGY_CONVERSIONS.find(
                                (item) => item.backendTypeCast === a.creationCauseTo
                              )?.label
                            }
                          </span>
                        ) : (
                          <span>
                            {
                              SPLITTING_STRATEGY_CONVERSIONS.find(
                                (item) => item.backendTypeCast === a.creationCauseTo
                              )?.label
                            }
                          </span>
                        )}
                      </Box>
                    )}
                  />
                </Table>
              )}
            </AutoSizer>
          </div>

          <TablePaginationControls
            currentPage={currentPage}
            setPage={setCurrentPage}
            isLastPage={isLastPage}
            startFromZero={false}
          />
        </>
      )}
    </div>
  )
}
